import { Dispatch } from "redux";
import { FormItem, CalculationData } from "models";
import { getToken } from "services/token.service";
import { addToLog } from "redux/LogBox/LogBox.actions";
import { SET_CONFIGS, CHECK_DATA } from "endpoints";

const configuration = (window as any).configuration;
const AWS_URL = configuration.awsUrl;

export enum FuelCompositionActions {
  SetFormItemValue = "fuelComposition/set_item_value",
  SetSubmitSuccessful = "fuelComposition/set_submit_successful",
  SetSubmitFailed = "fuelComposition/set_submit_failed",
  SetSubmitPending = "fuelComposition/set_pending",
  SetFormData = "fuelComposition/set_fuelComposition",
}

export interface SetPendingAction {
  type: string;
  payload: boolean;
}

export interface SetFormItemValueAction {
  type: string;
  payload: { id: string; value: number };
}

export interface SetSubmitSuccessfulAction {
  type: FuelCompositionActions.SetSubmitSuccessful;
}

export interface SetSubmitFailedAction {
  type: FuelCompositionActions.SetSubmitFailed;
}

export interface SetFormDataAction {
  type: FuelCompositionActions.SetFormData;
  payload: FormItem[];
}

export interface SetDateTimeAction {
  type: string;
  payload: { date: Date; offset: number };
}

export type FormActionTypes = SetPendingAction | SetFormItemValueAction | SetDateTimeAction | SetFormDataAction;

const submitSuccessful = (): SetSubmitSuccessfulAction => {
  return {
    type: FuelCompositionActions.SetSubmitSuccessful,
  };
};

const submitFailed = (): SetSubmitFailedAction => {
  return {
    type: FuelCompositionActions.SetSubmitFailed,
  };
};

const setSubmitPending = (pending: boolean): SetPendingAction => {
  return {
    type: FuelCompositionActions.SetSubmitPending,
    payload: pending,
  };
};

export const setFormData = (form: FormItem[]): SetFormDataAction => {
  return {
    type: FuelCompositionActions.SetFormData,
    payload: form,
  };
};

export const loadDefaults = () => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await fetch("../defaults.json");
      const data = await response.json();

      if (!response.ok) {
        throw new Error("Unable to load default values");
      }

      dispatch(setFormData(data));
    } catch (error) {
      console.error(error);
    }
  };
};

export const submitForm = (preparedForm: CalculationData, checkData: boolean) => {
  const form = JSON.stringify(preparedForm);
  const { millId, lineId, startTime, endTime } = preparedForm;

  return async (dispatch: Dispatch) => {
    dispatch(setSubmitPending(true));

    try {
      const token = await getToken();

      // Check if data exists for the selected timeframe
      // Data should not be checked if damper configs are loaded
      if (checkData) {
        const dataCheckResult = await fetch(AWS_URL + CHECK_DATA, {
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: token as string,
          }),
          method: "post",
          body: JSON.stringify({ millId, lineId, startTime, endTime }),
        });

        if (!dataCheckResult.ok) {
          const errorMsg = await dataCheckResult.text();
          throw new Error(errorMsg);
        }
      }

      // After checking the timeframe, we can proceed to submit the form
      const response = await fetch(AWS_URL + SET_CONFIGS, {
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: token as string,
        }),
        method: "post",
        body: form,
      });

      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg);
      }

      dispatch(submitSuccessful());
      dispatch(addToLog({ text: "Submitted successfully, calculation started", severity: "success", data: preparedForm }));
    } catch (error) {
      console.error(error);
      dispatch(submitFailed());
      dispatch(
        addToLog({
          text: `${(error as any).message ?? "Something went wrong"}, calculation not started`,
          severity: "error",
          data: preparedForm,
        }),
      );
    }
  };
};

export const setFormItemValue = (id: string, value: number): SetFormItemValueAction => {
  return {
    type: FuelCompositionActions.SetFormItemValue,
    payload: { id: id, value: value },
  };
};
